import { useEffect, useRef, useState } from "react";
import {
  getPlaylist,
  listContent,
  renamePlaylist,
  setPlaylistItems,
  type PlaylistDetail,
} from "../lib/resources.ts";
import { DEFAULT_SLIDE_SECONDS } from "../lib/defaults.ts";
import type { Content } from "../lib/types.ts";
import ContentPreview from "./ContentPreview.tsx";

interface Item {
  contentId: string;
  durationSeconds: number;
  name: string;
  type: Content["type"];
  url: string | null;
  data: Content["data"];
}

// Edit a playlist's ordered items: add from the content pool, set durations,
// reorder, remove. Saves the full list via PUT /playlists/:id/items.
export default function PlaylistEditor({
  playlistId,
  onClose,
  autoFocusName = false,
}: {
  playlistId: string;
  onClose: () => void;
  /** Set for a just-created playlist: focus and select the name so it can be typed over. */
  autoFocusName?: boolean;
}) {
  const [detail, setDetail] = useState<PlaylistDetail | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [pool, setPool] = useState<Content[]>([]);
  const [name, setName] = useState("");
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Runs after `detail` lands, because the input isn't mounted until then.
  useEffect(() => {
    if (autoFocusName && detail) nameRef.current?.select();
  }, [autoFocusName, detail]);

  useEffect(() => {
    (async () => {
      try {
        const [d, c] = await Promise.all([getPlaylist(playlistId), listContent()]);
        setDetail(d);
        setName(d.name);
        setItems(
          d.items.map((it) => ({
            contentId: it.contentId,
            durationSeconds: it.durationSeconds,
            name: it.name,
            type: it.type,
            url: it.url,
            data: it.data,
          })),
        );
        setPool(c);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load playlist");
      }
    })();
  }, [playlistId]);

  const asContent = (it: Item): Content => ({
    id: it.contentId,
    name: it.name,
    type: it.type,
    url: it.url,
    data: it.data,
    createdBy: null,
    createdByName: null,
    createdByEmail: null,
    expiresAt: null,
    archivedAt: null,
    suggestedExpiry: null,
    createdAt: "",
  });

  function move(index: number, dir: -1 | 1) {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  }
  function setDuration(index: number, seconds: number) {
    setItems((prev) =>
      prev.map((it, i) =>
        i === index ? { ...it, durationSeconds: Math.max(1, seconds) } : it,
      ),
    );
  }
  function removeItem(index: number) {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }
  function addFromPool(c: Content) {
    setItems((prev) => [
      ...prev,
      {
        contentId: c.id,
        durationSeconds: DEFAULT_SLIDE_SECONDS,
        name: c.name,
        type: c.type,
        url: c.url,
        data: c.data,
      },
    ]);
    setPicking(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (detail && name.trim() && name.trim() !== detail.name) {
        await renamePlaylist(playlistId, name.trim());
      }
      await setPlaylistItems(
        playlistId,
        items.map((it) => ({
          contentId: it.contentId,
          durationSeconds: it.durationSeconds,
        })),
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
      setSaving(false);
    }
  }

  if (!detail) {
    return (
      <section className="admin-page">
        {error ? <div className="banner banner--error">{error}</div> : <p className="muted">Loading…</p>}
        <button className="link-btn" onClick={onClose}>← Back</button>
      </section>
    );
  }

  const totalSeconds = items.reduce((s, it) => s + it.durationSeconds, 0);
  // An import owns this list: it rebuilds its own slides on every sync and
  // parks everything else behind them. Saying so is the difference between
  // "my slide vanished" and "my slide plays after the deck, by design".
  const sourceOwned = detail?.sourceOwned === true;

  return (
    <section className="admin-page">
      <button className="link-btn" onClick={onClose}>← Back to playlists</button>
      <div className="page-head">
        <input
          ref={nameRef}
          className="title-input"
          aria-label="Playlist name"
          placeholder="Playlist name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="page-actions">
          <button className="btn btn--ghost" onClick={() => setPicking(true)}>
            + Add content
          </button>
          <button className="btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save playlist"}
          </button>
        </div>
      </div>
      <p className="muted">
        {items.length} item{items.length === 1 ? "" : "s"} · loops every{" "}
        {totalSeconds}s
      </p>

      {sourceOwned && (
        <div className="banner">
          This playlist is kept up to date by an import. The imported slides are
          replaced every time the deck changes, and anything you add here plays
          after them — saving keeps it that way, so your own slides can’t be
          overwritten by next week’s import.
        </div>
      )}

      {error && <div className="banner banner--error">{error}</div>}

      {items.length === 0 ? (
        <div className="admin-empty">
          <strong>Empty playlist</strong>
          <span>Add content to build the loop.</span>
        </div>
      ) : (
        <ol className="pl-items">
          {items.map((it, i) => (
            <li className="pl-item card" key={`${it.contentId}-${i}`}>
              <div className="pl-item-order">
                <button className="icon-btn" disabled={i === 0} onClick={() => move(i, -1)}>
                  ↑
                </button>
                <button
                  className="icon-btn"
                  disabled={i === items.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </button>
              </div>
              <div className="pl-item-preview">
                <ContentPreview content={asContent(it)} />
              </div>
              <div className="pl-item-name">
                {it.name}
                <span className="badge">{it.type}</span>
              </div>
              <label className="pl-item-dur">
                <input
                  type="number"
                  min={1}
                  value={it.durationSeconds}
                  onChange={(e) => setDuration(i, Number(e.target.value))}
                />
                <span>sec</span>
              </label>
              <button className="link-btn link-btn--danger" onClick={() => removeItem(i)}>
                Remove
              </button>
            </li>
          ))}
        </ol>
      )}

      {picking && (
        <div className="modal-scrim" onClick={() => setPicking(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add content</h2>
            {pool.length === 0 ? (
              <p className="muted">No content yet — create some in the Content tab.</p>
            ) : (
              <div className="picker-grid">
                {pool.map((c) => (
                  <button key={c.id} className="picker-item" onClick={() => addFromPool(c)}>
                    <div className="picker-preview">
                      <ContentPreview content={c} />
                    </div>
                    <span className="picker-name" title={c.name}>{c.name}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn--ghost" onClick={() => setPicking(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
